
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Report } from '@/types/report';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export const usePreviousReports = (isOpen: boolean, userEmail?: string) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  const fetchReports = useCallback(async () => {
    if (!userEmail) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('student_development_reports')
        .select('id, created_at, student_nickname, student_full_name, subject, teaching_date, teaching_time_slot, teaching_location, teaching_type, teaching_duration, student_id, student_grade_level, pinned')
        .eq('submitter_email', userEmail)
        .order('pinned', { ascending: false }) // Always order by pinned first in the modal
        .order('created_at', { ascending: false })
        .limit(10); // Limit to 10 most recent reports
      
      if (error) throw error;
      
      if (Array.isArray(data)) {
        setReports(data as Report[]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const togglePinReport = async (report: Report) => {
    if (!userEmail) return;
    
    try {
      const { error } = await supabase
        .from('student_development_reports')
        .update({ pinned: !report.pinned })
        .eq('id', report.id)
        .eq('submitter_email', userEmail); // Make sure user can only update their own reports
      
      if (error) throw error;
      
      // Show success toast
      toast({
        title: report.pinned ? "นำออกจากรายการที่ปักหมุด" : "เพิ่มเข้ารายการที่ปักหมุด",
        description: `รายงานวันที่ ${format(new Date(report.teaching_date), 'dd/MM/yyyy')} ${report.pinned ? "ถูกนำออกจาก" : "ถูกเพิ่มเข้า"}รายการที่ปักหมุดแล้ว`,
        duration: 2000,
      });
      
      // Update reports list immediately
      fetchReports();
      
    } catch (error) {
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถอัปเดตสถานะการปักหมุดได้",
        variant: "destructive",
      });
      console.error("Error toggling pin status:", error);
    }
  };

  useEffect(() => {
    if (isOpen && userEmail) {
      fetchReports();
    }
  }, [isOpen, userEmail, fetchReports]);

  return {
    reports,
    loading,
    error,
    togglePinReport,
    fetchReports
  };
};
